import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2 } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import { listCoupons, removeCoupon } from '../../utils/queries';

const PAGE_SIZE = 20;

const formatDiscount = (coupon) =>
	coupon.discountType === 'percentage'
		? `${Number(coupon.discountValue)}%`
		: `${Number(coupon.discountValue)} ${coupon.discountUnit === 'points' ? 'pts' : coupon.discountUnit === 'currency' ? 'USD' : coupon.discountUnit}`;

const isExpired = (coupon) => coupon.endsAt && new Date(coupon.endsAt).getTime() < Date.now();
const isNotStarted = (coupon) => coupon.startsAt && new Date(coupon.startsAt).getTime() > Date.now();
const isExhausted = (coupon) => coupon.usageLimit != null && coupon.usageCount >= coupon.usageLimit;

export default function CouponsList() {
	const { activeStoreId } = useContext(MyContext);
	const navigate = useNavigate();
	const [coupons, setCoupons] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);

	const refresh = () => {
		if (!activeStoreId) return;
		setLoading(true);
		listCoupons({
			storeId: activeStoreId,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
		}).then((result) => {
			if (result?.success) {
				setCoupons(result.data.coupons);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId, page, search]);

	const handleRemove = async (id, e) => {
		e.stopPropagation();
		await removeCoupon(id);
		refresh();
	};

	const columns = [
		{
			key: 'code',
			header: 'Code',
			render: (value, row) => (
				<button
					type='button'
					className='text-black font-medium hover:underline'
					onClick={() => navigate(`/coupons/${row.couponId}/edit`)}
				>
					{value?.toUpperCase()}
				</button>
			),
		},
		{ key: 'discountValue', header: 'Discount', sortable: false, render: (_, row) => formatDiscount(row) },
		{ key: 'scopeType', header: 'Scope', render: (value) => (value === 'store' ? 'Entire store' : value) },
		{
			key: 'usageCount',
			header: 'Usage',
			sortable: false,
			render: (value, row) => `${value}${row.usageLimit != null ? ` / ${row.usageLimit}` : ''}`,
		},
		{
			key: 'endsAt',
			header: 'Status',
			sortable: false,
			render: (_, row) => {
				if (isExhausted(row)) return <StatusBadge status='inactive' text='Usage limit reached' />;
				if (isExpired(row)) return <StatusBadge status='inactive' text='Expired' />;
				if (isNotStarted(row)) return <StatusBadge status='pending' text='Not started' />;
				return <StatusBadge status='active' text='Active' />;
			},
		},
		{
			key: 'couponId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' onClick={(e) => handleRemove(id, e)}>
					<Trash2 size={20} />
				</button>
			),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Coupon Codes</h1>
				<button type='button' className='btn-primary' disabled={!activeStoreId} onClick={() => navigate('/coupons/new')}>
					New Coupon
				</button>
			</div>

			<div className='mb-5'>
				<label>Search (code)</label>
				<div className='relative max-w-sm'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</div>

			<DataTable columns={columns} data={coupons} loading={loading} emptyHeading='No coupons yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
